<template>
  <div class="w-100 card-wrapper">
    <div v-if="reference && (paymentIsSuccess || paymentIsIntent)">
      <div v-if="paymentIsSuccess">
        <strong>IMPORTANT:</strong> Your payment was successful
      </div>
      <div v-else-if="paymentIsIntent">
        <strong>IMPORTANT:</strong> Your payment is processing
      </div>
    </div>
    <div v-else class="w-100 card-block">
      <b-alert show variant="danger" v-if="hasCardErrors" id="cardErrorMessage">
        <p>
          <strong>{{ trans.get('account.important') }}:</strong>
          {{ cardError }}
        </p>
        <p>Invoice was <b>not</b> paid. <b>Check your card details.</b></p>
        <p>You can try to pay it again <span v-if="invoiceLink">or see the invoice <a :href="invoiceLink">here</a></span></p>
      </b-alert>
      <div class="group">
        <div class="row">
          <div class="col-12">
            <label class="w-100">
              <span>{{ trans.get('checkout.credit_card_number') }}</span>
              <div id="card-number-element" :class="{ field: true, 'stripe-element-custom': true, disabled: loading }">
              </div>
              <div class="card-errors" role="alert">{{ this.cardNumberErrors }}</div>
            </label>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6 col-12">
            <label class="w-100">
              <span>{{ trans.get('checkout.expiry_date') }}</span>
              <div id="card-expiry-element" :class="{ field: true, 'stripe-element-custom': true, disabled: loading }" >
              </div>
              <div class="card-errors" role="alert">{{ this.cardExpiryErrors }}</div>
            </label>
          </div>
          <div class="col-md-6 col-12">
            <label class="w-100">
              <span>{{ trans.get('checkout.cvc2_number') }}</span>
              <div id="card-cvc-element" :class="{ field: true, 'stripe-element-custom': true, disabled: loading }">
              </div>
              <div class="card-errors" role="alert">{{ this.cardCvcErrors }}</div>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapFields} from "vuex-map-fields";

export default {
  name: 'credit-card-stripe',
  props: {
    isActive: Boolean,
    publicKey: String,
  },
  data() {
    return {
      style: {
        base: {
          color: '#32325d',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      },
      cardNumber: null,
      cardCvc: null,
      cardExpiry: null,
      cardNumberCompleted: false,
      cardExpiryCompleted: false,
      cardCvcCompleted: false,
      cardNumberErrors: null,
      cardExpiryErrors: null,
      cardCvcErrors: null,
      stripe: null,
      elements: null,
      hasBody: true,
      hasCardErrors: false,
      cardError: null,
      invoiceLink: null,
    }
  },
  computed: {
    ...mapFields([
      'invoices',
      'client_secret',
      'currency',
      'errors',
      'errorsMessagesFromApi',
      'selectedPaymentMethod',
      'order.order_id',
      'order',
      'payment',
      'loading',
      'paymentStatus',
      'reference',
      'bookingButtonLabel',
      'payButtonLabel',
    ]),
    isValid() {
      return this.cardNumberCompleted
          && this.cardExpiryCompleted
          && this.cardCvcCompleted;
    },
    paymentIsIntent() {
      return this.paymentStatus === 'PAYMENT_INTENT'
    },
    paymentIsSuccess() {
      return this.paymentStatus === 'PAYMENT_SUCCESS'
    },
  },
  methods: {
    cardNumberErrorsHandler: function (event) {
      this.cardNumberErrors = event.error ? event.error.message : '';
    },
    cardExpiryErrorsHandler: function (event) {
      this.cardExpiryErrors = event.error ? event.error.message : '';
    },
    cardCvcErrorsHandler: function (event) {
      this.cardCvcErrors = event.error ? event.error.message : '';
    },
    purchase() {
      let self = this;
      self.hasCardErrors = false;
      self.cardError = null;
      this.stripe.confirmCardPayment(
          this.client_secret,
          {
            payment_method: {
              card: this.cardNumber
            }
          }
      ).then((result) => {
        if (result.error) {
          self.hasCardErrors = true;
          self.cardError = result.error.message;
          self.bookingButtonLabel = this.trans.get('checkout.pay_again');
          self.payButtonLabel = this.trans.get('checkout.pay_again');
          self.loading = false;
          setTimeout(function() {
            window.scrollTo({
              top: document.getElementById('cardErrorMessage').style.top,
              left: 0,
              behavior: "smooth",
            });
          }, 500);
        } else {
          self.$emit('success-booking');
        }
      });
    },
    createOrder() {
      let self = this;
      //we already tried to pay but card failed
      if (self.hasCardErrors) {
        self.purchase();
      }
      else {
        self.order.payment_method = self.selectedPaymentMethod;
        self.invoiceLink = null;
        axios.post(window.create_order, this.order).then((response) => {
          self.invoices = [response.data.invoice.invoice_number];
          self.invoiceLink = `/en/account/invoice/details/${ response.data.invoice.id }`
          self.client_secret = response.data.paymentData.intent_id;
          self.purchase();
        }).catch((error) => {
          self.errorsMessagesFromApi = error.response.data.errors;
          self.loading = false;
        });
      }
    },
    createPaymentIntent() {
      let self = this;
      if (self.hasCardErrors) {
        self.purchase();
      }
      else {
        self.invoiceLink = null;
        self.payment.payment_method = self.selectedPaymentMethod
        self.payment.invoices = this.pluck(self.invoices, 'invoice_number')
        self.payment.currency = self.currency.id
        axios.post(window.payment_intent_url, this.payment).then((response) => {
          self.client_secret = response.data.intent_id;
          self.purchase();
        }).catch((error) => {
          self.errors = error.response.data.errors;
        });
      }
    },
    pluck(objs, name) {
      var sol = [];
      for (var i in objs) {
        if (objs[i].hasOwnProperty(name)) {
          sol.push(objs[i][name]);
        }
      }
      return sol;
    },
    setCardElement() {
      var self = this;
      this.stripe = Stripe(this.publicKey)
      this.elements = this.stripe.elements();
      this.cardNumber = this.elements.create('cardNumber', {
        style: this.style,
        placeholder: 'Card number',
      });
      this.cardNumber.mount('#card-number-element');
      this.cardNumber.on('change', function (event) {
        self.cardNumberCompleted = event.complete;
        self.cardNumberErrorsHandler(event);
      });
      this.cardExpiry = this.elements.create('cardExpiry', {
        style: this.style,
        placeholder: 'MM/YY',
      });
      this.cardExpiry.mount('#card-expiry-element');
      this.cardExpiry.on('change', function (event) {
        self.cardExpiryCompleted = event.complete;
        self.cardExpiryErrorsHandler(event);
      });
      this.cardCvc = this.elements.create('cardCvc', {
        style: this.style,
        placeholder: 'CVC',
      });
      this.cardCvc.mount('#card-cvc-element');
      this.cardCvc.on('change', function (event) {
        self.cardCvcCompleted = event.complete;
        self.cardCvcErrorsHandler(event);
      });
    },
  },
  mounted() {
    if (!(this.reference && (this.paymentIsSuccess || this.paymentIsIntent))) {
      this.setCardElement();
    }
  }
};
</script>

<style scoped>
.StripeElement, .stripe-element-custom {
  box-sizing: border-box;
  border: 1px solid #e4e4e4;
  border-radius: 3px;
  padding: 11px 0 11px 11px;
}

.card-wrapper {
  min-height: 50px;
}

.StripeElement--focus {
  border-color: #a1a1a1;
  box-shadow: 0 0 2px 2px #4d90fe;
}

.card-errors {
  color: #eb1c26;
}

div.field {
  background-color: #fff;
}

div.field.disabled {
  cursor: default;
  background-color: #E9ECEF;
}

button {
  border: none;
  border-radius: 4px;
  outline: none;
  text-decoration: none;
  color: #fff;
  background: #32325d;
  white-space: nowrap;
  display: inline-block;
  height: 40px;
  line-height: 40px;
  padding: 0 14px;
  box-shadow: 0 4px 6px rgba(50, 50, 93, .11), 0 1px 3px rgba(0, 0, 0, .08);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.025em;
  -webkit-transition: all 150ms ease;
  transition: all 150ms ease;
  float: left;
  margin-left: 12px;
}

button:hover {
  transform: translateY(-1px);
  box-shadow: 0 7px 14px rgba(50, 50, 93, .10), 0 3px 6px rgba(0, 0, 0, .08);
  background-color: #43458b;
}

.card-block {
  float: left;
}

</style>
